import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Music, Loader2 } from 'lucide-react';
import { logger } from '../lib/logger';
import { useAsyncOperation } from '../hooks/useAsyncOperation';
import { api } from '../lib/api';

interface AudioDownloaderProps {
  audioUrl: string | null;
  originalFileName: string;
}

type AudioFormat = 'wav' | 'mp3' | 'aiff';

export function AudioDownloader({ audioUrl, originalFileName }: AudioDownloaderProps) {
  const { loading: isConverting, error, execute } = useAsyncOperation();

  const handleDownload = async (format: AudioFormat) => {
    if (!audioUrl) return;

    try {
      await execute(async () => {
        // Get the base name without extension
        const baseName = originalFileName.replace(/\.[^/.]+$/, '');
        
        // Call the conversion endpoint
        const response = await api.post('/functions/v1/convert-audio', {
          url: audioUrl,
          format
        }, {
          responseType: 'blob'
        });

        // Create download link
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${baseName}_mastered.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);

        logger.info('Audio downloaded successfully', {
          format,
          fileName: a.download
        });
      }, {
        successMessage: 'Audio downloaded successfully'
      });
    } catch (err) {
      // Error is handled by useAsyncOperation
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-blue-300">Download Options</h3>
        {isConverting && (
          <div className="flex items-center gap-2 text-sm text-blue-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Converting...
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {(['wav', 'mp3', 'aiff'] as AudioFormat[]).map((format) => (
          <motion.button
            key={format}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleDownload(format)}
            disabled={isConverting || !audioUrl}
            className="flex items-center justify-center gap-2 px-4 py-2 
                     bg-gray-800/50 rounded-lg border border-gray-700
                     hover:bg-gray-700/50 hover:border-blue-500/30
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all duration-200"
          >
            <Download className="w-4 h-4" />
            <span className="uppercase">{format}</span>
          </motion.button>
        ))}
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-3 text-sm text-red-400 bg-red-900/20 rounded-lg border border-red-500/20"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
}